import { formatMoney } from '../../lib/formatters.ts'
import type { ForecastVersion, ForecastWeek } from '../../api/cashReport'
import { reconcileForecastView } from './reconcileForecastView.js'
import { formatCalendarMonthDay } from '../../lib/localDate.ts'

export type HeadlineKey = 'today' | 'this_week' | 'next_week' | 'lowest'

export type Headline = {
  key: HeadlineKey
  label: string
  weekStart: string | null
  dollars: number | null
  formatted: string
  sublabel: string
  danger: boolean
  sameWeek?: boolean
  state?: 'covered' | 'tight' | 'below'
}

function monthDayShort(iso: string): string {
  return formatCalendarMonthDay(iso) || iso
}

function weekDollars(w?: ForecastWeek | null): number | null {
  if (!w) return null
  if (w.close && Number.isFinite(w.close.amount)) return w.close.amount
  if (w.close_cents != null) return Number(w.close_cents) / 100
  return null
}

function coveredLabel(w?: ForecastWeek | null): string {
  if (!w) return 'See projects'
  if (w.below_zero || w.below_cushion) return 'Tight · see projects'
  return 'Covered · see projects'
}

export function buildHeadlines(version: ForecastVersion): Headline[] {
  const weeks = version.payload?.weeks || []
  const todayDollars = version.opening_cash_cents != null
    ? Number(version.opening_cash_cents) / 100
    : null
  const thisWeek = weeks[0]
  const nextWeek = weeks[1]
  let lowest = weeks[0] || null
  for (const w of weeks) {
    const a = weekDollars(w)
    const b = weekDollars(lowest)
    if (a != null && (b == null || a < b)) lowest = w
  }
  const bankAsOf = version.bank_as_of
    ? monthDayShort(String(version.bank_as_of).slice(0, 10))
    : null
  const todaySub = bankAsOf ? `As of ${bankAsOf}` : 'Open bank sources'
  const lowDollars = weekDollars(lowest)
  const lowestIsThisWeek = !!(lowest && thisWeek && lowest.week_start === thisWeek.week_start)
  return [
    {
      key: 'today',
      label: 'Cash today',
      weekStart: thisWeek?.week_start || null,
      dollars: todayDollars,
      formatted: todayDollars == null ? '—' : formatMoney(todayDollars),
      sublabel: todaySub,
      danger: false,
    },
    {
      key: 'this_week',
      label: 'This week',
      weekStart: thisWeek?.week_start || null,
      dollars: weekDollars(thisWeek),
      formatted: weekDollars(thisWeek) == null ? '—' : formatMoney(weekDollars(thisWeek)),
      sublabel: coveredLabel(thisWeek),
      danger: !!(thisWeek && (thisWeek.below_zero || thisWeek.below_cushion)),
    },
    {
      key: 'next_week',
      label: 'Next week',
      weekStart: nextWeek?.week_start || null,
      dollars: weekDollars(nextWeek),
      formatted: weekDollars(nextWeek) == null ? '—' : formatMoney(weekDollars(nextWeek)),
      sublabel: coveredLabel(nextWeek),
      danger: !!(nextWeek && (nextWeek.below_zero || nextWeek.below_cushion)),
    },
    {
      key: 'lowest',
      label: 'Lowest point',
      weekStart: lowest?.week_start || null,
      dollars: lowDollars,
      formatted: lowDollars == null ? '—' : formatMoney(lowDollars),
      sublabel: lowestIsThisWeek
        ? 'This week is the lowest point'
        : (lowest ? `${monthDayShort(lowest.week_start)} · see causes` : 'See causes'),
      danger: !!(lowest && (lowest.below_zero || (lowDollars != null && lowDollars < 0))),
      sameWeek: lowestIsThisWeek,
      state: !lowest
        ? 'covered'
        : lowest.below_zero || (lowDollars != null && lowDollars < 0)
          ? 'below'
          : lowest.below_cushion
            ? 'tight'
            : 'covered',
    },
  ]
}

export function applyOptimisticAssumptions<T extends { kind?: string; amount_cents?: number | null; effective_date?: string | null }>(
  rows: T[],
  overlay: { cents: number; asOf: string } | null,
): T[] {
  if (!overlay || !Array.isArray(rows)) return rows || []
  let patched = false
  return rows.map((r) => {
    if (r.kind !== 'cash_on_hand' || patched) return r
    patched = true
    return { ...r, amount_cents: overlay.cents, effective_date: overlay.asOf || r.effective_date }
  })
}

export function applyOptimisticBank(
  headlines: Headline[],
  overlay: { cents: number; asOf: string } | null,
): Headline[] {
  if (!overlay) return headlines
  const dollars = overlay.cents / 100
  const asOf = monthDayShort(overlay.asOf)
  return headlines.map((h) => (h.key === 'today'
    ? { ...h, dollars, formatted: formatMoney(dollars), sublabel: `As of ${asOf}` }
    : h))
}

export function weekOutDollars(w: ForecastWeek): number {
  const parts = [w.subs_out, w.other_out, w.job_costs_out, w.payroll, w.overhead]
  return parts.reduce((s, m) => s + (m && Number.isFinite(m.amount) ? m.amount : 0), 0)
}

export function equation30d(
  version: ForecastVersion,
  overlay?: { cents: number; asOf?: string } | null,
) {
  const view = reconcileForecastView(version, overlay)
  return {
    cashNow: view.cashNow,
    expectedIn: view.expectedIn,
    dueOut: view.dueOut,
    in30: view.in30,
  }
}
